import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ReferencesCarousel, useOrderedReferences } from '@bonusly/common-performance-recap';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	ChevronDownOutline,
	ClappingHandsOutline,
	Heading,
	HStack,
	rem,
	Spacer,
	Text,
	Tooltip,
} from '@bonusly/design-system';

import { useCurrentUserRole } from '../../hooks/use-current-user-role';
import { MeetingDTO } from '../../lib/entities/meeting';
import { DiscussionIdeaCard } from './discussion-idea-card';
import { useReferenceMessages } from './hooks/use-reference-messages';

type MeetingAgendaProps = {
	meeting: MeetingDTO;
};

export const ReferencesAccordion: FC<MeetingAgendaProps> = ({ meeting }) => {
	const { bonuses, feedback, isError, isLoading } = useReferenceMessages(meeting);
	const orderedReferences = useOrderedReferences(bonuses, feedback);
	const { t } = useTranslation(['one_on_ones', 'common']);
	const { isGuest } = useCurrentUserRole(meeting.event);

	if (isError) return null;

	const tooltipLabel = isGuest
		? t('tooltips.meeting.carousel.direct_report')
		: t('tooltips.meeting.carousel.manager', {
				direct_report_name: meeting.event?.guest?.display_first_name,
			});

	const postsLength = orderedReferences.length;
	const suggestedTopic = meeting.suggestedTopics?.[0];
	const suggestedTopicCategoryId = meeting.alignmentCategory?.id;
	const enableDiscussionIdeaCard = bonuses && suggestedTopic && suggestedTopicCategoryId;

	return (
		<Accordion allowToggle bg="bg.canvas" borderRadius="xl" defaultIndex={0} p={{ base: 2, md: 4 }} mb={rem(20)} data-component="references-accordion">
			<AccordionItem id="item" border="none">
				{({ isExpanded }) => (
					<>
						<AccordionButton p={0} id="button" borderRadius="md" _hover={{ bg: 'bg.canvas' }}>
							<HStack w="full">
								<ClappingHandsOutline boxSize={rem(32)} color="points.fgOnSurface" />
								<Tooltip label={tooltipLabel} placement="top" hasArrow fontSize={50} openDelay={300}>
									<Heading size="xl">{t('meeting.carousel.title')}</Heading>
								</Tooltip>
								<Spacer />
								{!isLoading &&
									(postsLength > 0 ? (
										<>
											{!isExpanded &&
												t('meeting.carousel.post_count', {
													count: postsLength,
												})}
											<AccordionIcon as={ChevronDownOutline} boxSize={rem(32)} />
										</>
									) : (
										<Text alignContent="center" color="text.softest">
											{t('meeting.carousel.empty_state')}
										</Text>
									))}
							</HStack>
						</AccordionButton>
						<AccordionPanel p={0}>
							{!isLoading && postsLength > 0 && (
								// Layer a grid (resets flexbox) and block element (establishes a scroll area)
								// on top of the carousel to prevent it from overflowing the container and
								// to allow the carousel to read scroll data from the parent.
								<Box pt={rem(16)} minWidth="0" width="100%" maxWidth="100%" display="grid">
									<Box pt={rem(16)} minWidth="0" width="100%" maxWidth="100%">
										<ReferencesCarousel references={orderedReferences}>
											{enableDiscussionIdeaCard && (
												<DiscussionIdeaCard
													suggestedTopic={suggestedTopic}
													addToCategoryId={suggestedTopicCategoryId}
													meetingId={meeting.id}
													bonuses={bonuses}
												/>
											)}
										</ReferencesCarousel>
									</Box>
								</Box>
							)}
						</AccordionPanel>
					</>
				)}
			</AccordionItem>
		</Accordion>
	);
};
ReferencesAccordion.displayName = 'ReferencesAccordion';
